import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import PageShell from "../common/PageShell";
import utils from "../../universal/utils";
import { fetchMembershipStatus } from "../../redux/actions/catalogActions";
import {
  BILLING_CYCLES,
  TIERS,
  TIER_DISPLAY,
  YEARLY_RENEWAL_MODE,
} from "../../constants/membership";
import { buildScrubbedMembershipUrl } from "./membershipPaypalUtils";
import { buildReferralInviteLink } from "../../universal/referralLinks";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    textAlign: "center",
    padding: "2rem",
  },
  icon: {
    fontSize: "4rem",
    marginBottom: "1rem",
  },
}));

export default function MembershipSuccessPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const user = useSelector((state) => state.catalogReducer?.user);
  const membership = useSelector((state) => state.catalogReducer?.membership);

  const [status, setStatus] = useState("capturing");
  const [error, setError] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const [completedMembership, setCompletedMembership] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const subscriptionId = queryParams.get("subscription_id");
  const selectedTier = queryParams.get("tier");
  const selectedBillingCycle = queryParams.get("billingCycle");
  const renewalMode = queryParams.get("renewalMode");

  const callbackBillingCycle = subscriptionId
    ? BILLING_CYCLES.MONTHLY
    : token
      ? BILLING_CYCLES.YEARLY
      : null;

  const resolvedTier = completedMembership?.tier || selectedTier || membership?.tier;
  const resolvedBillingCycle =
    completedMembership?.billingCycle
    || selectedBillingCycle
    || callbackBillingCycle
    || membership?.billingCycle;

  const tierLabel = resolvedTier && resolvedTier !== TIERS.FREE
    ? TIER_DISPLAY[resolvedTier]?.name
    : null;
  const cycleLabel = resolvedBillingCycle === BILLING_CYCLES.MONTHLY
    ? "monthly"
    : resolvedBillingCycle === BILLING_CYCLES.YEARLY
      ? "yearly"
      : null;
  const planLabel = tierLabel && cycleLabel
    ? `${tierLabel} ${cycleLabel} membership`
    : tierLabel
      ? `${tierLabel} membership`
      : "membership";
  const isYearlyRenewal = renewalMode === YEARLY_RENEWAL_MODE && resolvedBillingCycle === BILLING_CYCLES.YEARLY;

  function getProcessingCopy() {
    if (tierLabel && isYearlyRenewal) {
      return {
        title: `Extending your ${tierLabel} plan...`,
        description: "Please wait while we confirm your payment and add another year to your membership.",
      };
    }

    if (tierLabel && resolvedBillingCycle === BILLING_CYCLES.MONTHLY) {
      return {
        title: `Activating your ${tierLabel} plan...`,
        description: "Please wait while we confirm your recurring PayPal subscription.",
      };
    }

    if (tierLabel && resolvedBillingCycle === BILLING_CYCLES.YEARLY) {
      return {
        title: `Confirming your ${tierLabel} plan...`,
        description: "Please wait while we capture your PayPal payment and activate your membership.",
      };
    }

    return {
      title: "Processing Your Payment...",
      description: "Please wait while we confirm your payment.",
    };
  }

  function getSuccessCopy() {
    if (tierLabel && isYearlyRenewal) {
      return {
        title: `${tierLabel} renewed!`,
        description: `Your ${planLabel} has been extended for another year. Your remaining access has been preserved and your new renewal date is active.`,
      };
    }

    if (tierLabel && resolvedBillingCycle === BILLING_CYCLES.MONTHLY) {
      return {
        title: `Welcome to ${tierLabel}!`,
        description: `Your ${planLabel} is now active. Recurring billing is set up and all ${tierLabel} features are ready to use.`,
      };
    }

    if (tierLabel && resolvedBillingCycle === BILLING_CYCLES.YEARLY) {
      return {
        title: `Welcome to ${tierLabel}!`,
        description: `Your ${planLabel} is now active. Enjoy full access to all ${tierLabel} features and elevated matchmaking.`,
      };
    }

    return {
      title: "Welcome Aboard!",
      description: "Your membership is now active. Enjoy your upgraded matchmaking experience.",
    };
  }

  const processingCopy = getProcessingCopy();
  const successCopy = getSuccessCopy();

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const scrubbedUrl = buildScrubbedMembershipUrl(
      location.pathname,
      location.search
    );
    const currentUrl = `${location.pathname}${location.search}`;

    if (scrubbedUrl !== currentUrl) {
      window.history.replaceState({}, "", scrubbedUrl);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!user?.userId) {
      setStatus("done");
      return;
    }

    if (!token && !subscriptionId) {
      setStatus("done");
      return;
    }

    // Monthly recurring: activate PayPal subscription
    if (subscriptionId) {
      activateSubscription(subscriptionId);
      return;
    }

    // Yearly one-time: capture PayPal order
    if (token) {
      capturePayment(token);
    }
  }, [subscriptionId, token, user?.userId, dispatch]);

  useEffect(() => {
    if (status !== "done" || !user?.userId) return;
    fetch(`${basePath}/api/v1/referral/my-code`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok && (data?.referralCode || data?.code))
          setReferralCode(data.referralCode || data.code);
      })
      .catch(() => {});
  }, [status, user?.userId]);

  async function capturePayment(orderId) {
    try {
      const res = await fetch(`${basePath}/api/v1/membership/paypal/capture`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });

      const data = await res.json();

      if (data.ok) {
        setCompletedMembership(data.membership || null);
        dispatch(fetchMembershipStatus(user.userId));
        setStatus("done");
      } else {
        setError(data.message || "Payment capture failed");
        setStatus("error");
      }
    } catch (err) {
      setError(err.message || "Network error");
      setStatus("error");
    }
  }

  async function activateSubscription(subscriptionId) {
    try {
      const res = await fetch(`${basePath}/api/v1/membership/paypal/subscription-activated`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscriptionId }),
      });

      const data = await res.json();

      if (data.ok) {
        setCompletedMembership(data.membership || null);
        dispatch(fetchMembershipStatus(user.userId));
        setStatus("done");
      } else {
        setError(data.message || "Subscription activation failed");
        setStatus("error");
      }
    } catch (err) {
      setError(err.message || "Network error");
      setStatus("error");
    }
  }

  return (
    <PageShell maxWidth="md">
      <Box className={classes.root}>
        {status === "capturing" && (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#ffffff", mb: 1 }}>
              {processingCopy.title}
            </Typography>
            <Typography sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
              {processingCopy.description}
            </Typography>
          </>
        )}

        {status === "done" && (
          <>
            <CheckCircleIcon className={classes.icon} sx={{ color: "#4CAF50" }} />
            <Typography variant="h4" sx={{ fontWeight: 800, color: "#ffffff", mb: 1 }}>
              {successCopy.title}
            </Typography>
            <Typography sx={{ color: "rgba(255, 255, 255, 0.6)", mb: 3, maxWidth: 400 }}>
              {successCopy.description}
            </Typography>
            {referralCode && (
              <Box sx={{
                display: "flex", alignItems: "center", gap: 1,
                mb: 2, px: 2, py: 1, borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.85rem" }}>
                  Love Ouro? Copy your personal invite link for a friend.
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(buildReferralInviteLink(referralCode)).then(() => {
                      setReferralCopied(true);
                      setTimeout(() => setReferralCopied(false), 2000);
                    }).catch(() => {});
                  }}
                  sx={{ color: referralCopied ? "#4CAF50" : "rgba(255,255,255,0.5)", p: 0.5 }}
                >
                  {referralCopied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={() => history.push("/lobby")}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #E8A33D 0%, #E8A33D 100%)",
                }}
              >
                Start Playing
              </Button>
              <Button
                variant="outlined"
                onClick={() => history.push("/account")}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "rgba(255, 255, 255, 0.2)",
                  color: "rgba(255, 255, 255, 0.7)",
                }}
              >
                View Account
              </Button>
            </Box>
          </>
        )}

        {status === "error" && (
          <>
            <Alert severity="error" sx={{ mb: 3, maxWidth: 500, borderRadius: 2 }}>
              {error || "Something went wrong with your payment."}
            </Alert>
            <Button
              variant="outlined"
              onClick={() => history.push("/membership/plans")}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                borderColor: "rgba(255, 255, 255, 0.2)",
                color: "rgba(255, 255, 255, 0.7)",
              }}
            >
              Try Again
            </Button>
          </>
        )}
      </Box>
    </PageShell>
  );
}
